import { useCallback, useEffect, useRef, useState } from 'react';
import {
  breathPositionAt,
  type BreathPosition,
  type BreathworkPattern,
} from '@mlm/shared/coaching';
import { BREATH_SCALE } from './breath-scale';

export type BreathSessionStatus = 'idle' | 'running' | 'paused' | 'done';

/** How often the screen re-reads the clock. The ring itself animates in CSS. */
const TICK_MS = 150;

/** The ring's size at `position`: growing on the inhale, full on its hold, shrinking on the exhale. */
function scaleAt(position: BreathPosition): number {
  const progress = position.phaseMs > 0 ? position.phaseElapsedMs / position.phaseMs : 1;
  const span = BREATH_SCALE.in - BREATH_SCALE.out;
  switch (position.phase) {
    case 'inhale':
      return BREATH_SCALE.out + span * progress;
    case 'holdIn':
      return BREATH_SCALE.in;
    case 'exhale':
      return BREATH_SCALE.in - span * progress;
    default:
      return BREATH_SCALE.out;
  }
}

/** Where the ring is heading by the end of `position`'s phase. */
function targetScale(position: BreathPosition): number {
  return position.phase === 'inhale' || position.phase === 'holdIn'
    ? BREATH_SCALE.in
    : BREATH_SCALE.out;
}

/**
 * A breathing session driven by the wall clock, not by counting ticks.
 *
 * The session remembers when it (virtually) started and asks the shared
 * `breathPositionAt` where elapsed time puts the member, so a throttled
 * background tab or a locked phone lands later in the SAME session instead of
 * drifting behind the ring. Pausing shifts the start forward by the pause.
 *
 * `ring` is what the ring should do: while running, head for the end of the
 * phase over the time the phase had LEFT when it first showed (so a phase seen
 * a tick late still ends on time); while paused, hold exactly where it is.
 */
export function useBreathSession(pattern: BreathworkPattern, cycles: number) {
  const [status, setStatus] = useState<BreathSessionStatus>('idle');
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);
  const pausedElapsed = useRef(0);
  const phase = useRef({ key: '', durationMs: 0 });

  useEffect(() => {
    if (status !== 'running') return;
    const read = () => {
      const now = Date.now() - startedAt.current;
      setElapsed(now);
      if (breathPositionAt(pattern, cycles, now).done) setStatus('done');
    };
    read();
    const timer = window.setInterval(read, TICK_MS);
    return () => window.clearInterval(timer);
  }, [status, pattern, cycles]);

  const start = useCallback(() => {
    startedAt.current = Date.now();
    pausedElapsed.current = 0;
    setElapsed(0);
    setStatus('running');
  }, []);

  const pause = useCallback(() => {
    pausedElapsed.current = Date.now() - startedAt.current;
    setElapsed(pausedElapsed.current);
    setStatus('paused');
  }, []);

  const resume = useCallback(() => {
    startedAt.current = Date.now() - pausedElapsed.current;
    setStatus('running');
  }, []);

  /** Back to the start, ready to begin again. */
  const reset = useCallback(() => {
    pausedElapsed.current = 0;
    setElapsed(0);
    setStatus('idle');
  }, []);

  const position = breathPositionAt(pattern, cycles, elapsed);
  // Captured once per (status, round, phase): the ring's transition must not be
  // re-timed on every tick, or it would keep stretching the same animation.
  const key = `${status}:${position.cycle}:${position.phase}`;
  if (phase.current.key !== key) {
    phase.current = {
      key,
      durationMs: status === 'running' ? position.phaseMs - position.phaseElapsedMs : 0,
    };
  }
  const ring =
    status === 'running'
      ? { scale: targetScale(position), durationMs: phase.current.durationMs }
      : status === 'paused'
        ? { scale: scaleAt(position), durationMs: 0 }
        : { scale: BREATH_SCALE.out, durationMs: status === 'done' ? 600 : 0 };

  return { status, position, ring, start, pause, resume, reset };
}
